import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import {
  Typography,
  Paper,
  Alert,
  Box,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import apiService from '../../../services/api';
import { DATE_FORMATS } from '../../../constants';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import CustomLoading from '../../../components/common/CustomLoading';
import RideRouteMap from './RideRouteMap';
import { useNotification } from '../../../contexts/NotificationContext';
import { formatMobileNumber } from '../../../utils/formatters';
import { useAppSelector } from '../../../hooks/useAuth';

interface RideDetails {
  id: string;
  bookingId: string;
  dueDate: string;
  userName: string;
  mobileNo: string;
  vehicleName: string;
  vehicleNumber?: string;
  registrationNumber?: string;
  traveled: string;
  isOverdue: boolean;
  startDate?: string;
  endDate?: string;
  status?: string;
  plan?: string;
  hubName?: string;
  totalAmount?: number;
  paidAmount?: number;
  remainingAmount?: number;
  notes?: string;
  tripStatus?: number;
  planStartDate?: string;
  planEndDate?: string;
  bookingAmount?: number;
  tripStart?: TripStart;
  tripEnd?: TripEnd;
  sosLogs?: SOSLog[];
}

interface TripStart {
  tripmasterid: number;
  vehiclephotos: string;
  batteryphotos: string;
  chargerphotos: string;
  e_signature: string;
  terms_condition_accepted: boolean;
  tripstatus: number;
  triptype: number;
  userid: number;
  vehiclechecklistnote: string;
  createdby: number;
  createdon: string;
  modifiedby: number;
  modifiedon: string;
  bookingmasterid: number;
  receiveragentname: string | null;
  bookingDetails: any;
  vehicleconditionchecklistmappings: VehicleConditionChecklistMapping[] | null;
}

interface TripEnd {
  tripmasterid: number;
  vehiclephotos: string;
  batteryphotos: string;
  chargerphotos: string;
  e_signature: string;
  terms_condition_accepted: boolean;
  tripstatus: number;
  triptype: number;
  userid: number;
  vehiclechecklistnote: string;
  createdby: number;
  createdon: string;
  modifiedby: number;
  modifiedon: string;
  bookingmasterid: number;
  receiveragentname: string | null;
  bookingDetails: any;
  vehicleconditionchecklistmappings: VehicleConditionChecklistMapping[] | null;
}

interface VehicleConditionChecklistMapping {
  mappingid: number;
  tripmasterid: number;
  vehicleconditionchecklistid: number;
  vehicleconditioncheckedstatus: boolean;
  checklistname: string;
  checklistdescription: string | null;
  checklistcode: string | null;
  checklisttype: number;
}

interface SOSLog {
  soslogid: number;
  bookingmasterid: number;
  latitude: string;
  longitude: string;
  alertsentemailid: string | null;
  name: string;
  phonenumber: string;
  useremailid: string | null;
  createdon: string;
}

interface ApiRideDetailsResponse {
  bookingmasterid: number;
  name: string;
  phonenumber: string;
  hubname: string;
  planname: string;
  tripstatus: number;
  planstartdate: string;
  planenddate: string;
  bookingamount: number;
  vehiclename: string;
  vehiclenum?: string;
  registrationnumber?: string;
  vehiclelongitude: number | null;
  vehiclelatitude: number | null;
  evtypeid: number;
  traveledkm: number | null;
  vehicletypeid: number;
  tripStart: TripStart | null;
  tripEnd: TripEnd | null;
  hublongitude: number[] | null;
  hublatitude: number[] | null;
  termacceptedpdf?: string | null;
  soslogslist?: SOSLog[];
}

const RideDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [rideDetails, setRideDetails] = useState<RideDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [abscondDialogOpen, setAbscondDialogOpen] = useState(false);
  const [absconding, setAbsconding] = useState(false);
  const { showNotification } = useNotification();
  const { user } = useAppSelector((state) => state.auth);
  const canAbscond = user?.roleid && [1, 2, 7, 10].includes(user.roleid);

  useDocumentTitle();

  useEffect(() => {
    if (id) {
      fetchRideDetails();
    }
  }, [id]);

  const fetchRideDetails = async () => {
    if (!id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.getRideDetails(id);
      
      let data: ApiRideDetailsResponse | null = null;
      
      // Handle different response structures
      if (response && typeof response === 'object') {
        if ('data' in response) {
          // Response wrapped in data property
          if (Array.isArray(response.data) && response.data.length > 0) {
            data = response.data[0];
          } else if (!Array.isArray(response.data) && response.data) {
            data = response.data;
          }
        } else if (Array.isArray(response) && response.length > 0) {
          // Response is directly an array
          data = response[0];
        } else if (!Array.isArray(response) && 'bookingmasterid' in response) {
          // Response is directly the object
          data = response as ApiRideDetailsResponse;
        }
      }
      
      if (data) {
        const planEndDate = new Date(data.planenddate);
        const currentDate = new Date();
        const isOverdue = planEndDate < currentDate;
        
        // Format traveled distance
        const traveledDistance = data.traveledkm !== null && data.traveledkm !== undefined 
          ? `${data.traveledkm} km` 
          : 'N/A';
        
        // Helper function to handle null, undefined, and empty strings
        const getValueOrNA = (value: any): string => {
          if (value === null || value === undefined || value === "") {
            return "N/A";
          }
          return String(value);
        };

        const transformedDetails: RideDetails = {
          id: data.bookingmasterid.toString(),
          bookingId: data.bookingmasterid.toString(),
          dueDate: data.planenddate,
          userName: data.name,
          mobileNo: data.phonenumber,
          vehicleName: data.vehiclename,
          vehicleNumber: getValueOrNA(data.vehiclenum),
          registrationNumber: getValueOrNA(data.registrationnumber),
          traveled: traveledDistance,
          isOverdue: isOverdue,
          startDate: data.planstartdate,
          endDate: data.planenddate,
          status: getTripStatusText(data.tripstatus),
          plan: data.planname,
          hubName: data.hubname,
          totalAmount: data.bookingamount,
          tripStatus: data.tripstatus,
          planStartDate: data.planstartdate,
          planEndDate: data.planenddate,
          bookingAmount: data.bookingamount,
          tripStart: data.tripStart || undefined,
          tripEnd: data.tripEnd || undefined,
          sosLogs: data.soslogslist || [],
        };
        
        setRideDetails(transformedDetails);
      } else {
        setError('No ride details found');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch ride details');
    } finally {
      setLoading(false);
    }
  };

  const getTripStatusText = (status: number): string => {
    const statusMap: Record<number, string> = {
      1: 'Pending',
      2: 'In Progress',
      3: 'Completed',
      4: 'Cancelled',
      5: 'Started',
    };
    return statusMap[status] || '';
  };

  const handleAbscondClick = () => {
    setAbscondDialogOpen(true);
  };

  const handleAbscondCancel = () => {
    setAbscondDialogOpen(false);
  };

  const handleAbscondConfirm = async () => {
    if (!rideDetails) return;
    
    setAbsconding(true);
    try {
      await apiService.abscondUser(rideDetails.bookingId);
      showNotification('User marked as absconded successfully', 'success');
      setAbscondDialogOpen(false);
      // Optionally refresh ride details
      fetchRideDetails();
    } catch (err: any) {
      showNotification(err.message || 'Failed to mark user as absconded', 'error');
    } finally {
      setAbsconding(false);
    }
  };



  if (loading) {
    return <CustomLoading message="Fetching Ride Details..." />;
  }

  if (error) {
    return (
      <Container fluid className="px-0">
        <Alert severity="error" className="mb-3">
          {error}
        </Alert>
      </Container>
    );
  }

  if (!rideDetails) {
    return (
      <Container fluid className="px-0">
        <Alert severity="info" className="mb-3">
          No ride details found
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="px-0">
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'rgba(22, 67, 135, 1)' }}>
            Ride Details
          </Typography>
          {rideDetails.isOverdue && (
            <Chip
              label="OVERDUE"
              sx={{
                backgroundColor: 'transparent',
                color: '#d32f2f',
                fontWeight: 600,
                border: '1px solid #d32f2f',
              }}
            />
          )}
        </Box>
        {canAbscond && rideDetails.isOverdue && (
          <Button
            variant="contained"
            color="error"
            onClick={handleAbscondClick}
            sx={{
              backgroundColor: '#d32f2f',
              '&:hover': {
                backgroundColor: '#b71c1c',
              },
            }}
          >
            Abscond
          </Button>
        )}
      </Box>

      <Row className="g-4">
        <Col xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'rgba(22, 67, 135, 1)' }}>
              Basic Information
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Booking ID
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {rideDetails.bookingId}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  User Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {rideDetails.userName}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Mobile Number
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {formatMobileNumber(rideDetails.mobileNo) || 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Vehicle Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {rideDetails.vehicleName}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Vehicle Number
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {rideDetails.vehicleNumber || 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Registration Number
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {rideDetails.registrationNumber || 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Distance Traveled
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {rideDetails.traveled || 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Col>

        <Col xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'rgba(22, 67, 135, 1)' }}>
              Dates & Status
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Due Date
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontWeight: 500,
                    color: rideDetails.isOverdue ? '#d32f2f' : 'inherit'
                  }}
                >
                  {DATE_FORMATS.formatDate(rideDetails.dueDate)}
                </Typography>
              </Box>
              {rideDetails.startDate && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Start Date
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {DATE_FORMATS.formatDate(rideDetails.startDate)}
                  </Typography>
                </Box>
              )}
              {rideDetails.endDate && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    End Date
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {DATE_FORMATS.formatDate(rideDetails.endDate)}
                  </Typography>
                </Box>
              )}
              {rideDetails.status && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip 
                    label={rideDetails.status} 
                    color="primary" 
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              )}
            </Box>
          </Paper>
        </Col>

        <Col xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'rgba(22, 67, 135, 1)' }}>
              Plan & Hub Information
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {rideDetails.plan && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Plan
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {rideDetails.plan}
                  </Typography>
                </Box>
              )}
              {rideDetails.hubName && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Hub Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {rideDetails.hubName}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Col>

        <Col xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'rgba(22, 67, 135, 1)' }}>
              Payment Information
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {rideDetails.totalAmount !== undefined && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Amount
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    ₹{rideDetails.totalAmount.toLocaleString()}
                  </Typography>
                </Box>
              )}
              {rideDetails.paidAmount !== undefined && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Paid Amount
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: '#2e7d32' }}>
                    ₹{rideDetails.paidAmount.toLocaleString()}
                  </Typography>
                </Box>
              )}
              {rideDetails.remainingAmount !== undefined && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Remaining Amount
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: 500,
                      color: rideDetails.remainingAmount > 0 ? '#d32f2f' : '#2e7d32'
                    }}
                  >
                    ₹{rideDetails.remainingAmount.toLocaleString()}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Col>

        {rideDetails.notes && (
          <Col xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'rgba(22, 67, 135, 1)' }}>
                Notes
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {rideDetails.notes}
              </Typography>
            </Paper>
          </Col>
        )}

        {rideDetails.sosLogs && rideDetails.sosLogs.length > 0 && (
          <Col xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'rgba(22, 67, 135, 1)' }}>
                SOS Logs
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: 'rgba(191, 229, 255, 1)' }}>SOS Log ID</TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: 'rgba(191, 229, 255, 1)' }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: 'rgba(191, 229, 255, 1)' }}>Phone Number</TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: 'rgba(191, 229, 255, 1)' }}>Created On</TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: 'rgba(191, 229, 255, 1)' }}>Location</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rideDetails.sosLogs.map((log) => {
                      const googleMapsUrl = `https://www.google.com/maps?q=${log.latitude},${log.longitude}`;
                      return (
                        <TableRow key={log.soslogid}>
                          <TableCell>{log.soslogid}</TableCell>
                          <TableCell>{log.name}</TableCell>
                          <TableCell>{log.phonenumber}</TableCell>
                          <TableCell>
                            {log.createdon ? new Date(log.createdon).toLocaleString() : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Link
                              href={googleMapsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                color: 'rgba(22, 67, 135, 1)',
                                textDecoration: 'none',
                                '&:hover': {
                                  textDecoration: 'underline',
                                },
                              }}
                            >
                              View on Google Maps
                            </Link>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Col>
        )}

        <Col xs={12}>
          <RideRouteMap bookingId={rideDetails.bookingId} />
        </Col>
      </Row>

      {/* Abscond Confirmation Dialog */}
      <Dialog
        open={abscondDialogOpen}
        onClose={handleAbscondCancel}
        aria-labelledby="abscond-dialog-title"
        aria-describedby="abscond-dialog-description"
      >
        <DialogTitle id="abscond-dialog-title">
          Confirm Abscond
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="abscond-dialog-description">
            Are you sure you want to mark this user as absconded? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleAbscondCancel}
            disabled={absconding}
            sx={{ color: "rgba(13, 44, 90, 1)" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAbscondConfirm}
            disabled={absconding}
            variant="contained"
            sx={{
              backgroundColor: "rgba(197, 0, 0, 1)",
              "&:hover": { backgroundColor: "rgba(197, 0, 0, 0.8)" }
            }}
          >
            {absconding ? <CircularProgress size={20} color="inherit" /> : "Yes"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RideDetailsPage;
